import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../providers/auth_provider';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { formatDate } from '../../../utils/format_date';

export default function AffiliateSettings() {
    const { token } = useAuth();
    const [settings, setSettings] = useState([]);
    const [newTier1Commission, setNewTier1Commission] = useState('');
    const [newTier2Commission, setNewTier2Commission] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [newPayoutPrice, setNewPayoutPrice] = useState('');
    const [editPayoutPrice, setEditPayoutPrice] = useState('');
    const [editTier1Commission, setEditTier1Commission] = useState('');
    const [editTier2Commission, setEditTier2Commission] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');

    useEffect(() => {
        fetchSettings();
    }, []);

    // Fetch settings
    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/affiliate/settings`, {
                headers: {
                    Signature: `${process.env.REACT_APP_API_SIGNATURE}`,
                    Authorization: `Bearer ${token}`,
                },
            });
            setSettings(response.data);
        } catch (error) {
            console.error('Error fetching affiliate settings:', error);
        }
    };

    // Open modal for create or edit
    const handleOpenModal = (setting = null) => {
        if (setting) {
            // Edit mode
            setIsEditing(true);
            setEditId(setting.id);
            setEditTier1Commission(setting.tier1_commission.toString());
            setEditTier2Commission(setting.tier2_commission.toString());
            setEditPayoutPrice(setting.payout_price.toString()); // Add this line
            setModalTitle('Edit Commission Setting');
        } else {
            // Create mode
            setIsEditing(false);
            setEditId(null);
            setEditTier1Commission('');
            setEditTier2Commission('');
            setEditPayoutPrice(''); // Reset this value when creating a new setting
            setNewTier1Commission('');
            setNewTier2Commission('');
            setNewPayoutPrice('');
            setModalTitle('Add New Commission Setting');
        }
        setShowModal(true);
    };

    // Close modal
    const handleCloseModal = () => {
        setShowModal(false);
        setNewTier1Commission('');
        setNewTier2Commission('');
        setEditTier1Commission('');
        setEditTier2Commission('');
        setEditId(null);
        setIsEditing(false);
    };

    // Create setting
    const handleCreateSetting = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/admin/affiliate/settings`, {
                tier1_commission: parseFloat(newTier1Commission),
                tier2_commission: parseFloat(newTier2Commission),
                payout_price: parseFloat(newPayoutPrice), // Add payout price here
            },
                {
                    headers: {
                        Signature: `${process.env.REACT_APP_API_SIGNATURE}`,
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setNewTier1Commission('');
            setNewTier2Commission('');
            setNewPayoutPrice(''); // Reset the payout price
            setShowModal(false);
            toast.success('Commission setting created successfully');
            fetchSettings();
        } catch (error) {
            console.error('Error creating affiliate setting:', error);
        }
    };


    // Update setting
    const handleUpdateSetting = async (e) => {
        e.preventDefault();
        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}/admin/affiliate/settings/${editId}`,
                {
                    tier1_commission: parseFloat(editTier1Commission),
                    tier2_commission: parseFloat(editTier2Commission),
                    payout_price: parseFloat(editPayoutPrice), // Add payout price here
                },
                {
                    headers: {
                        Signature: `${process.env.REACT_APP_API_SIGNATURE}`,
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setIsEditing(false);
            setEditId(null);
            toast.success('Commission setting updated successfully');
            setEditTier1Commission('');
            setEditTier2Commission('');
            setEditPayoutPrice(''); // Reset the payout price
            setShowModal(false);
            fetchSettings();
        } catch (error) {
            console.error('Error updating affiliate setting:', error);
        }
    };


    return (
        <>
            <Helmet>
                <title>Affiliate Settings - Enervybe</title>
            </Helmet>
            <div className="card">
                <div className="card-header align-items-center border-bottom">
                    <div className="d-flex justify-content-between">
                        <h5 className="card-title">Affiliate Commission Settings </h5>
                        {settings.length < 1 && <button className="btn btn-sm btn-label-primary waves-effect waves-light" onClick={() => handleOpenModal()}>
                            <i className="ri-add-line"></i> Add New Setting
                        </button>}
                    </div>
                </div>
                <div className="card-body p-0">
                    {/* Settings Table */}
                    <div className="table-responsive">
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Tier 1 Commission (%)</th>
                                    <th>Tier 2 Commission (%)</th>
                                    <th>Min. Payout Price ($)</th>
                                    <th>Created At</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {settings.map((setting, index) => (
                                    <tr key={setting.id}>
                                        <td>{index + 1}</td>
                                        <td>{`${setting.tier1_commission}%`}</td>
                                        <td>{`${setting.tier2_commission}%`}</td>
                                        <td>{`$${setting.payout_price}`}</td>
                                        <td>
                                            {formatDate(setting.created_at)}
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-primary"
                                                onClick={() => handleOpenModal(setting)}
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Create/Edit Modal */}
            <div
                className={`modal fade ${showModal ? 'show' : ''}`}
                style={{ display: showModal ? 'block' : 'none' }}
                tabIndex="-1"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{modalTitle}</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={handleCloseModal}
                            ></button>
                        </div>
                        <form onSubmit={isEditing ? handleUpdateSetting : handleCreateSetting}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="tier1Commission" className="form-label">
                                        Tier 1 Commission Rate (%)
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="tier1Commission"
                                        value={isEditing ? editTier1Commission : newTier1Commission}
                                        onChange={(e) =>
                                            isEditing
                                                ? setEditTier1Commission(e.target.value)
                                                : setNewTier1Commission(e.target.value)
                                        }
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="tier2Commission" className="form-label">
                                        Tier 2 Commission Rate (%)
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="tier2Commission"
                                        value={isEditing ? editTier2Commission : newTier2Commission}
                                        onChange={(e) =>
                                            isEditing
                                                ? setEditTier2Commission(e.target.value)
                                                : setNewTier2Commission(e.target.value)
                                        }
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="payoutPrice" className="form-label">
                                        Payout Price ($)
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="payoutPrice"
                                        value={isEditing ? editPayoutPrice : newPayoutPrice}
                                        onChange={(e) =>
                                            isEditing
                                                ? setEditPayoutPrice(e.target.value)
                                                : setNewPayoutPrice(e.target.value)
                                        }
                                        step="0.01"
                                        min="0"
                                        required
                                    />
                                </div>
                                <small className="form-text text-muted">
                                    This is the minimum amount required to make a payout.
                                </small>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCloseModal}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {showModal && <div className="modal-backdrop fade show"></div>}
        </>
    );
}
